import React, { useContext, useEffect, useMemo, useReducer, useState } from 'react';
import Chart from 'react-google-charts';
import axios from 'axios';
import { Store } from '../Store';
import { getError } from '../utils';
import LoadingBox from '../components/LoadingBox';
import MessageBox from '../components/MessageBox';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';

const formatMonth = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

const getDefaultPeriod = () => {
  const now = new Date();
  return {
    fromMonth: formatMonth(new Date(now.getFullYear(), now.getMonth() - 2, 1)),
    toMonth: formatMonth(now),
  };
};

const monthIndex = (month) => {
  const [year, number] = month.split('-').map(Number);
  return year * 12 + number - 1;
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        summary: action.payload,
        loading: false,
      };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
};
export default function DashboardScreen() {
  const [{ loading, summary, error }, dispatch] = useReducer(reducer, {
    loading: true,
    error: '',
  });
  const { state } = useContext(Store);
  const { userInfo } = state;
  const [{ fromMonth, toMonth }, setPeriod] = useState(getDefaultPeriod);
  const validMonth = /^\d{4}-(0[1-9]|1[0-2])$/;
  const periodError =
    !validMonth.test(fromMonth) || !validMonth.test(toMonth)
      ? 'Please select both a start and an end month.'
      : fromMonth > toMonth
      ? 'The start month must be on or before the end month.'
      : '';

  const monthlySales = useMemo(() => {
    if (!summary || periodError) {
      return [];
    }

    const salesByMonth = new Map();
    summary.dailyOrders.forEach(({ _id, sales }) => {
      const month = _id.slice(0, 7);
      if (month >= fromMonth && month <= toMonth) {
        salesByMonth.set(month, (salesByMonth.get(month) || 0) + sales);
      }
    });

    const firstMonth = monthIndex(fromMonth);
    return Array.from(
      { length: monthIndex(toMonth) - firstMonth + 1 },
      (_, offset) => {
        const index = firstMonth + offset;
        const month = `${String(Math.floor(index / 12)).padStart(4, '0')}-${String(
          (index % 12) + 1
        ).padStart(2, '0')}`;
        const label = new Date(`${month}-01T00:00:00`).toLocaleDateString(
          'en-US',
          { month: 'short', year: 'numeric' }
        );
        return [label, salesByMonth.get(month) || 0];
      }
    );
  }, [summary, fromMonth, toMonth, periodError]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await axios.get('/api/orders/summary', {
          headers: { Authorization: `Bearer ${userInfo.token}` },
        });
        dispatch({ type: 'FETCH_SUCCESS', payload: data });
      } catch (err) {
        dispatch({
          type: 'FETCH_FAIL',
          payload: getError(err),
        });
      }
    };
    fetchData();
  }, [userInfo]);

  return (
    <div>
      <h1>Dashboard</h1>
      {loading ? (
        <LoadingBox />
      ) : error ? (
        <MessageBox variant="danger">{error}</MessageBox>
      ) : (
        <>
          <p className="text-muted">All-time totals</p>
          <Row>
            <Col md={4}>
              <Card>
                <Card.Body>
                  <Card.Title>
                    {summary.users && summary.users[0]
                      ? summary.users[0].numUsers
                      : 0}
                  </Card.Title>
                  <Card.Text> Users</Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card>
                <Card.Body>
                  <Card.Title>
                    {summary.orders && summary.orders[0]
                      ? summary.orders[0].numOrders
                      : 0}
                  </Card.Title>
                  <Card.Text> Orders</Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card>
                <Card.Body>
                  <Card.Title>
                    {summary.orders && summary.orders[0]
                      ? summary.orders[0].totalSales.toFixed(2)
                      : 0} EGP
                  </Card.Title>
                  <Card.Text> Sales</Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <div className="my-3">
            <h2>Sales</h2>
            <p className="text-muted">
              Monthly sales in EGP. Both selected months are included.
            </p>
            <Row className="g-3 align-items-end mb-3">
              <Col xs={12} sm={4}>
                <Form.Group controlId="sales-from-month">
                  <Form.Label>From month</Form.Label>
                  <Form.Control
                    type="month"
                    value={fromMonth}
                    max={toMonth || undefined}
                    required
                    aria-describedby={
                      periodError ? 'sales-period-error' : undefined
                    }
                    aria-invalid={Boolean(periodError)}
                    onChange={(event) =>
                      setPeriod({ fromMonth: event.target.value, toMonth })
                    }
                  />
                </Form.Group>
              </Col>
              <Col xs={12} sm={4}>
                <Form.Group controlId="sales-to-month">
                  <Form.Label>To month</Form.Label>
                  <Form.Control
                    type="month"
                    value={toMonth}
                    min={fromMonth || undefined}
                    required
                    aria-describedby={
                      periodError ? 'sales-period-error' : undefined
                    }
                    aria-invalid={Boolean(periodError)}
                    onChange={(event) =>
                      setPeriod({ fromMonth, toMonth: event.target.value })
                    }
                  />
                </Form.Group>
              </Col>
              <Col xs={12} sm={4}>
                <Button
                  variant="outline-primary"
                  onClick={() => setPeriod(getDefaultPeriod())}
                >
                  Last 3 months
                </Button>
              </Col>
            </Row>
            {periodError ? (
              <div id="sales-period-error">
                <MessageBox variant="danger">{periodError}</MessageBox>
              </div>
            ) : (
              <>
                {monthlySales.every(([, sales]) => sales === 0) && (
                  <MessageBox>No sales in the selected period.</MessageBox>
                )}
                <Chart
                  width="100%"
                  height="400px"
                  chartType="ColumnChart"
                  loader={<div>Loading Chart...</div>}
                  data={[['Month', 'Sales (EGP)'], ...monthlySales]}
                  options={{
                    legend: { position: 'none' },
                    colors: ['#0d6efd'],
                    chartArea: { left: 85, right: 25, top: 25, bottom: 80 },
                    hAxis: { title: 'Month', slantedText: true },
                    vAxis: {
                      title: 'Sales (EGP)',
                      minValue: 0,
                      format: '#,##0.##',
                    },
                    bar: { groupWidth: '55%' },
                  }}
                />
              </>
            )}
          </div>
          <div className="my-3">
            <h2>Categories</h2>
            {summary.productCategories.length === 0 ? (
              <MessageBox>No Category</MessageBox>
            ) : (
              <Chart
                width="100%"
                height="400px"
                chartType="PieChart"
                loader={<div>Loading Chart...</div>}
                data={[
                  ['Category', 'Products'],
                  ...summary.productCategories.map((x) => [x._id, x.count]),
                ]}
              ></Chart>
            )}
          </div>
        </>
      )}
    </div>
  );
}